import SearchParser from './search_parser';

export default class SearchQuery {
  constructor() {
    this.eventHandlers = {};
    this.searchText = '';
    this.tokens = [];
    this.selectedToken = null;
    this.selectionStart = 0;
    this.selectionEnd = 0;
    this.active = false;
    this.initialized = false;
    this.recursionLevel = 0;
  }

  on(event, fn) {
    if (!this.eventHandlers[event]) {
      this.eventHandlers[event] = [];
    }
    this.eventHandlers[event].push(fn);
  }

  off(event, fn) {
    let h;
    if ((h = this.eventHandlers[event])) {
      let i;
      if ((i = h.indexOf(fn) !== -1)) {
        h.splice(i, 1);
      }
    }
  }

  trigger(event, data) {
    // Prevent event recursion more than 2 levels deep
    if (this.recursionLevel <= 1) {
      this.recursionLevel += 1;
      (this.eventHandlers[event] || []).forEach(fn => fn(data));
      this.recursionLevel -= 1;
    }
  }

  update(searchText, selectionStart, selectionEnd) {
    let changed = false;
    if (searchText !== this.searchText) {
      this.tokens = SearchParser.tokenize(searchText);
      this.searchText = SearchParser.toString(this.tokens);
      changed = true;
    }
    if (
      (this.initialized && !this.selectedToken) ||
      selectionStart !== this.selectionStart ||
      selectionEnd !== this.selectionEnd
    ) {
      this.selectionStart = selectionStart;
      this.selectionEnd = selectionEnd;
      this.selectedToken = SearchParser.tokenAtCursor(
        this.tokens,
        selectionEnd,
      );
      changed = true;
    }
    if (changed) {
      this.trigger('change');
    }
    this.initialized = true;
  }

  stopEditing() {
    // Delete placeholders (i.e. "...") and set the selectedToken to nil
    const tokens = this.tokens.slice();
    const placeholders = Array.from(tokens).filter(t => t.text === '...');
    placeholders.forEach(p => tokens.splice(p.index, 2));
    this.commit(tokens);
  }

  // This method is only to be called by this class after a token removal, etc happens
  commit(input) {
    this.searchText =
      input instanceof Array ? SearchParser.toString(input) : input;
    this.tokens = SearchParser.tokenize(this.searchText);
    this.selectedToken = null;
    this.trigger('commit');
  }

  select(oldToken, cursorOffset) {
    [this.selectionStart, this.selectionEnd] = Array.from(
      this.cursor(oldToken),
    );
    this.selectionStart += cursorOffset || 0;
    this.selectedToken = SearchParser.tokenAtCursor(
      this.tokens,
      this.selectionEnd,
    );
    this.trigger('select', [this.selectionStart, this.selectionEnd]);
  }

  cursor(token) {
    if (!token) {
      return 0;
    }
    const start = token.startOffset + token.valueStartOffset();
    const end = token.endOffset + token.valueEndOffset();
    return [start, end];
  }

  deleteTokenAtCursor(cursor) {
    // We will update the ivars later all at once
    let t;
    const newTokens = this.tokens.slice();
    const token = SearchParser.tokenAtCursor(newTokens, cursor);
    // All non-whitespace tokens are followed by whitespace, except the last one.
    newTokens.splice(token.index, 2);
    // When deleting the last token, the preceeding whitespace is still there,
    // so we just remove all whitespace tokens from the end
    while ((t = newTokens[newTokens.length - 1]) && t.isWhitespace()) {
      newTokens.splice(newTokens.length - 1, 1);
    }
    this.commit(newTokens);
    this.select(
      this.tokens[token.index] || this.tokens[this.tokens.length - 1],
    );
    this.trigger('change');
  }

  replaceToken(replacementToken, cursorOffset) {
    const newTokens = SearchParser.replaceToken(
      this.searchText,
      replacementToken,
    );
    this.commit(newTokens);
    this.select(newTokens[replacementToken.index], cursorOffset);
    this.trigger('change');
  }

  prepend(textToPrepend) {
    if (!textToPrepend) {
      return;
    }
    this.commit(`${textToPrepend}\u2001${this.searchText}`);
    this.select(this.tokens[0]);
    this.trigger('change');
  }

  prependOrSelect(textToPrepend) {
    if (!textToPrepend) {
      return;
    }

    const token = this.tokens.find(t => t.text.startsWith(textToPrepend));

    if (token) {
      this.select(token);
      this.trigger('change');
    } else {
      this.prepend(textToPrepend);
    }
  }

  deleteTokensStartingWith(prefix) {
    this.commit(SearchParser.tokensNotStartingWith(this.searchText, prefix));
    this.trigger('change');
  }
}
